import {
  Component,
  EventEmitter,
  Inject,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import {
  ConfirmationDialogComponent,
  ConfirmationDialogProps,
  ConfirmDialogResult,
} from 'src/app/shared/components/modals/confirmation-dialog/confirmation-dialog.component';
import { PersonDocumentType, PersonType, ScreenStatus } from 'src/app/services/models/enums';
import { finalize, Subscription, take } from 'rxjs';
import { PageLoaderService } from 'src/app/services/page-loader.service';
import { GlobalServiceService } from 'src/app/services/global/global-service.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  CNPJValidate,
  CPFValidate,
  getDocumentMask as getDocumentMaskUtil,
} from 'src/app/helpers/documents-utils';
import { FormsModule, NgForm } from '@angular/forms';
import { deepCopy } from 'src/app/helpers/transformation-utils';
import { CommonModule } from '@angular/common';
import { NgxMaskDirective } from 'ngx-mask';
import { SharedModule } from 'src/app/shared/shared.module';
import { MatButtonModule } from '@angular/material/button';
import {
  CreateTrackingRegistryRequestDto,
  TrackingRegistryService,
  UpdateTrackingRegistryRequestDto,
} from 'src/generated-sources/openapi/wallet-service/tracking-registry';
import { WalletDataService } from 'src/app/services/data/wallet-data.service';
import { WalletResponseDto } from 'src/generated-sources/openapi/wallet-service/wallet';
import { followUpTimeOptions, NO_NAME } from '../../constants';
import { InputRegexDirective } from 'src/app/shared/directives/input-regex.directive';
import { isAlert30DaysWallet } from 'src/app/helpers/plan-utils';

type AditionalFields = {
  id?: string;
  name?: string;
  creationDate?: string;
};
export type TrackingRegistryRegisterDialogProps = Partial<CreateTrackingRegistryRequestDto> &
  AditionalFields;

export const trackingRegistryEmptyValues: TrackingRegistryRegisterDialogProps = {
  document: '',
  internalCode: '',
  segment: '',
  followUpType: 0,
  walletId: '',
  favorite: false,
};

@Component({
  selector: 'app-tracking-record-register-dialog',
  templateUrl: './tracking-record-register-dialog.component.html',
  styleUrls: ['./tracking-record-register-dialog.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    NgxMaskDirective,
    SharedModule,
    MatButtonModule,
    ConfirmationDialogComponent,
    InputRegexDirective,
  ],
})
export class TrackingRecordRegisterDialogComponent implements OnInit, OnDestroy {
  @Output() modelSendToRedirect = new EventEmitter();
  @ViewChild('inclusionForm') inclusionForm?: NgForm;

  followUpTime = followUpTimeOptions;
  selectedWalletId?: string = '';
  isSaving = false;
  submited = false;
  screenStatus = ScreenStatus.Creation;

  subscriptions: Subscription[] = [];
  personType = PersonType.COMPANY;
  documentText = PersonDocumentType.CNPJ;
  walletsList: WalletResponseDto[] = [];

  initialValues = deepCopy(trackingRegistryEmptyValues);
  trackingRegistry: TrackingRegistryRegisterDialogProps = deepCopy(trackingRegistryEmptyValues);

  constructor(
    @Inject(MAT_DIALOG_DATA)
    private readonly data: TrackingRegistryRegisterDialogProps,
    private readonly dialogRef: MatDialogRef<TrackingRecordRegisterDialogComponent>,
    private readonly trackingRegistryService: TrackingRegistryService,
    private readonly pageLoaderService: PageLoaderService,
    private readonly globalService: GlobalServiceService,
    private readonly walletDataService: WalletDataService,
    private readonly snackBar: MatSnackBar,
    private readonly dialog: MatDialog,
  ) {
    this.dialogRef.disableClose = true;

    if (data) {
      this.initialValues = deepCopy(data);
      this.trackingRegistry = {
        ...deepCopy(data),
        name: data.name || NO_NAME,
      };
      this.selectedWalletId = data.walletId;
      this.screenStatus = ScreenStatus.Edition;
    }
  }

  ngOnInit() {
    this.subscriptions.push(
      this.dialogRef.backdropClick().subscribe(() => {
        this.verifyChangesBeforeClose();
      }),
    );

    this.subscriptions.push(
      this.dialogRef.keydownEvents().subscribe((event: KeyboardEvent) => {
        if (event.key === 'Escape') this.verifyChangesBeforeClose();
      }),
    );

    this.setPersonType();
    this.initialValues = deepCopy({
      ...this.trackingRegistry,
      walletId: this.selectedWalletId,
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  setPersonType() {
    this.personType = this.globalService.getPersonTypeCurrentValue();

    if (this.personType === PersonType.PERSON) {
      this.documentText = PersonDocumentType.CPF;
    } else {
      this.documentText = PersonDocumentType.CNPJ;
    }
    this.updateWallets();
  }

  updateWallets() {
    this.walletDataService.getWalletsValue().forEach((wallet) => {
      if (!isAlert30DaysWallet(wallet.planId)) {
        this.walletsList.push(wallet);
      }
    });
  }

  getDocumentMask(): string {
    return getDocumentMaskUtil(this.personType);
  }

  getTitle() {
    return this.isCreating
      ? $localize`:@@followRegister:Incluir Documento`
      : $localize`:@@registerEdition:Editar Documento`;
  }

  getConfirmButtonText() {
    return this.isCreating ? $localize`:@@include:Incluir` : $localize`:@@save:Guardar`;
  }

  verifyChangesBeforeClose() {
    if (this.isSaving) return;

    if (this.hasAnyChange) {
      const confirmDialogRef = this.dialog.open(ConfirmationDialogComponent, {
        data: {
          title: $localize`:@@attention:Atención`,
          message: $localize`:@@noSavedInformation:Hay cambios que no se han guardado.`,
          cancelButton: {
            text: $localize`:@@goBack:Volver`,
          },
          confirmButton: {
            text: $localize`Descartar`,
          },
        },
        disableClose: true,
      });

      confirmDialogRef
        .afterClosed()
        .pipe(take(1))
        .subscribe((value) => {
          if (value === ConfirmDialogResult.Confirm) {
            this.trackingRegistry = { ...trackingRegistryEmptyValues };
            this.dialogRef.close(ConfirmDialogResult.Cancel);
            this.openSnackBar($localize`:@@discardedInformation:Información descartada con éxito`);
          }
        });
    } else {
      this.dialogRef.close(ConfirmDialogResult.Cancel);
    }
  }

  handleSubmit() {
    this.submited = true;

    this.inclusionForm?.form.markAllAsTouched();

    if (this.inclusionForm?.invalid) return;

    this.isSaving = true;
    this.pageLoaderService.setPageLoader(true);

    this.isCreating ? this.addTrackingRecord() : this.editTrackingRecord();
  }

  addTrackingRecord() {
    const createTrackingRegistryRequestDto: CreateTrackingRegistryRequestDto = {
      document: this.trackingRegistry.document,
      followUpType: this.trackingRegistry.followUpType,
      walletId: this.selectedWalletId,
      internalCode: this.trackingRegistry.internalCode?.trim() || null,
      segment: this.trackingRegistry.segment?.trim() || null,
      favorite: false,
    };

    this.trackingRegistryService
      .create({ createTrackingRegistryRequestDto })
      .pipe(
        finalize(() => {
          this.isSaving = false;
          this.pageLoaderService.setPageLoader(false);
        }),
      )
      .subscribe({
        next: (response) => {
          this.closeModalAndRedirectToRecords();
          this.openSnackBar(
            $localize`:@@documentSuccessfullyIncluded:Documento incluido con éxito`,
          );
        },
        error: ({ error }) => {
          let defaultMessage = $localize`:@@errorIncludingDocument:Error al incluir el documento`;

          if (error.message) {
            defaultMessage += '. ' + error.message;
          }

          this.openSnackBar(defaultMessage);
        },
      });
  }

  editTrackingRecord() {
    const updateTrackingRegistryRequestDto: UpdateTrackingRegistryRequestDto = {
      internalCode: this.trackingRegistry.internalCode?.trim() || null,
      segment: this.trackingRegistry.segment?.trim() || null,
    };

    this.trackingRegistryService
      .updateById({
        id: this.trackingRegistry.id,
        updateTrackingRegistryRequestDto,
      })
      .pipe(
        finalize(() => {
          this.isSaving = false;
          this.pageLoaderService.setPageLoader(false);
        }),
      )
      .subscribe({
        next: (response) => {
          this.openSnackBar($localize`:@@documentSuccessfullyEdited:Cambios realizados con éxito`);
          this.dialogRef.close(ConfirmDialogResult.Confirm);
        },
        error: (error) => {
          this.openSnackBar($localize`:@@errorEditingDocument:Error al realizar cambios`);
        },
      });
  }

  closeModalAndRedirectToRecords() {
    this.modelSendToRedirect.emit();
    this.globalService.setDocumentAdded();
    this.dialogRef.close(ConfirmDialogResult.Confirm);
  }

  handleCancel() {
    this.verifyChangesBeforeClose();
  }

  openSnackBar(message: string) {
    this.snackBar.open(message, $localize`:@@js_Close:Cerrar`, {
      duration: 7000,
    });
  }

  get hasAnyChange() {
    const { document, internalCode, segment } = this.trackingRegistry;

    const initialInternalCode = this.initialValues.internalCode?.trim() || null;
    const currentInternalCode = internalCode?.trim() || null;
    const initialSegment = this.initialValues.segment?.trim() || null;
    const currentSegment = segment?.trim() || null;

    return (
      this.initialValues.document !== document ||
      initialInternalCode !== currentInternalCode ||
      initialSegment !== currentSegment ||
      this.initialValues.walletId !== this.selectedWalletId
    );
  }

  get isCPFInvalid(): boolean {
    if (this.personType === PersonType.PERSON) {
      return !!this.trackingRegistry.document && !CPFValidate(this.trackingRegistry.document);
    }
    return false;
  }

  get isCNPJInvalid(): boolean {
    if (this.personType === PersonType.COMPANY) {
      return !!this.trackingRegistry.document && !CNPJValidate(this.trackingRegistry.document);
    }
    return false;
  }

  get invalidCPFCNPJMessage() {
    if (this.isCPFInvalid) return '* CPF inválido';
    if (this.isCNPJInvalid) return '* CNPJ inválido';
    return $localize`:@@mandatoryName:* Campo requerido`;
  }

  get isCreating() {
    return this.screenStatus === ScreenStatus.Creation;
  }

  get isEditing() {
    return this.screenStatus === ScreenStatus.Edition;
  }

  get confirmationDialogProps(): ConfirmationDialogProps {
    return {
      title: this.getTitle(),
      confirmButton: {
        text: this.getConfirmButtonText(),
        action: () => this.handleSubmit(),
        disabled: this.isSaving || !this.hasAnyChange || !this.selectedWalletId,
      },
      cancelButton: {
        text: $localize`:@@cancel:Cancelar`,
        action: () => this.handleCancel(),
      },
    };
  }
}
